import { useEffect, useMemo, useState } from "react";
import { Check, Link2, Loader2, Plus, Unlink, X } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import type {
  InboxView,
  InboxViewCriteria,
  InboxViewCriteriaInput,
  InboxViewFieldOp,
  InboxViewRule,
  InboxViewRuleDraft,
} from "@/types/inboxView/inboxView";
import { describeRule, RULE_SECTIONS, ruleSection, WEEKDAYS } from "./criteriaText";

type EditableRule = (InboxViewRule | InboxViewRuleDraft) & { key: string };

interface ViewCriteriaEditorProps {
  view: InboxView;
  isSaving: boolean;
  onSave: (patch: { prompt?: string; criteria?: InboxViewCriteriaInput }) => void;
}

const EMPTY_CRITERIA: InboxViewCriteria = { timezone: "UTC", threshold: 70, rules: [] };

const inputClass =
  "rounded-lg border border-[#E5EAF0] bg-white px-3 py-2 text-base sm:text-sm text-[#222B45] outline-none focus:border-genie-primary focus:ring-2 focus:ring-genie-primary/10";
const chipButtonClass =
  "inline-flex min-h-[44px] sm:min-h-0 items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors";

const toEditable = (rules: InboxViewRule[]): EditableRule[] => rules.map((r) => ({ ...r, key: r.id }));
const stripKey = ({ key: _key, ...rule }: EditableRule) => rule as InboxViewRule | InboxViewRuleDraft;

const FIELD_OPS: { value: InboxViewFieldOp; label: string; needsValue: boolean }[] = [
  { value: "filled", label: "is given", needsValue: false },
  { value: "empty", label: "is empty", needsValue: false },
  { value: "contains", label: "contains", needsValue: true },
  { value: "not_contains", label: "does not contain", needsValue: true },
  { value: "equals_any", label: "is one of (comma separated)", needsValue: true },
  { value: "gte", label: "is at least", needsValue: true },
  { value: "lte", label: "is at most", needsValue: true },
];

type AddKind = "ai" | "field" | "occurred" | "duration" | "caller_turns" | "deadline" | "age" | "score";

const ADD_OPTIONS: { value: AddKind; label: string }[] = [
  { value: "ai", label: "Something said in the conversation" },
  { value: "field", label: "A column's value" },
  { value: "occurred", label: "Day and time it happened" },
  { value: "duration", label: "How long it lasted" },
  { value: "caller_turns", label: "How much the caller said" },
  { value: "deadline", label: "A deadline the caller gave" },
  { value: "age", label: "Still waiting after a while" },
  { value: "score", label: "Urgency or buying intent score" },
];

/**
 * Editor for what a conversation has to pass to enter the view: the
 * conditions (text, time, score), how strict the match has to be, and the
 * view's description. Edits are local until saved.
 */
export const ViewCriteriaEditor = ({ view, isSaving, onSave }: ViewCriteriaEditorProps) => {
  const saved = view.criteria ?? EMPTY_CRITERIA;
  const [rules, setRules] = useState<EditableRule[]>(() => toEditable(saved.rules));
  const [threshold, setThreshold] = useState(saved.threshold);
  const [timezone, setTimezone] = useState(saved.timezone);
  const [prompt, setPrompt] = useState(view.prompt);
  const [adding, setAdding] = useState<AddKind | null>(null);
  // The plain-language condition whose wording is being edited in place.
  const [editing, setEditing] = useState<{ key: string; text: string } | null>(null);

  // Follow the server's copy whenever it changes (a save, a refine, a refresh).
  const savedKey = JSON.stringify(saved);
  useEffect(() => {
    setRules(toEditable(saved.rules));
    setThreshold(saved.threshold);
    setTimezone(saved.timezone);
    setEditing(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedKey]);
  useEffect(() => setPrompt(view.prompt), [view.prompt]);

  const rulesDirty = useMemo(
    () =>
      JSON.stringify(rules.map(stripKey)) !== JSON.stringify(saved.rules) ||
      threshold !== saved.threshold ||
      timezone !== saved.timezone,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rules, threshold, timezone, savedKey],
  );

  // Only day-and-hours conditions depend on the timezone, so it is offered with them.
  const usesClockTime =
    adding === "occurred" || rules.some((r) => r.kind === "time" && r.metric === "occurred");
  const timezones = useMemo(() => {
    const supported = typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : [];
    return supported.includes(timezone) ? supported : [timezone, ...supported];
  }, [timezone]);

  const commitEditing = () => {
    if (!editing) return;
    const text = editing.text.trim();
    if (text) patchRule(editing.key, { text });
    setEditing(null);
  };
  const promptDirty = prompt.trim() !== view.prompt.trim() && prompt.trim().length > 0;

  const patchRule = (key: string, patch: Partial<EditableRule>) =>
    setRules((prev) => prev.map((r) => (r.key === key ? ({ ...r, ...patch } as EditableRule) : r)));

  const removeRule = (key: string) => setRules((prev) => prev.filter((r) => r.key !== key));

  // Linking makes two neighbouring conditions "either one is enough".
  const toggleLink = (index: number) =>
    setRules((prev) => {
      const current = prev[index];
      const previous = prev[index - 1];
      if (!previous) return prev;
      if (current.group && current.group === previous.group) {
        const stillShared = prev.filter((r) => r.group === current.group).length > 2;
        return prev.map((r, i) => {
          if (i === index) return { ...r, group: undefined };
          return !stillShared && i === index - 1 ? { ...r, group: undefined } : r;
        });
      }
      const group = previous.group ?? `g${Date.now().toString(36)}`;
      return prev.map((r, i) => (i === index || i === index - 1 ? { ...r, group, must: previous.must } : r));
    });

  const addRule = (draft: InboxViewRuleDraft) => {
    setRules((prev) => [...prev, { ...draft, key: `new-${Date.now().toString(36)}-${prev.length}` }]);
    setAdding(null);
  };

  const saveRules = () => onSave({ criteria: { timezone, threshold, rules: rules.map(stripKey) } });

  const resetRules = () => {
    setRules(toEditable(saved.rules));
    setThreshold(saved.threshold);
    setTimezone(saved.timezone);
    setAdding(null);
    setEditing(null);
  };

  return (
    <div className="mt-4 border-t border-[#E5EAF0] pt-4 space-y-4">
      <div>
        <div className="text-[10px] uppercase tracking-wider font-semibold text-[#64748B]">What qualifies</div>
        <p className="mt-1 text-xs text-[#64748B]">
          A conversation enters this view only when it passes every must-have below and reaches the minimum match.
          Nice-to-have conditions raise or lower the match.
        </p>
      </div>

      {rules.length === 0 ? (
        <p className="text-sm text-[#64748B]">
          No conditions yet, so every conversation with something for the columns is included. Add one below.
        </p>
      ) : (
        RULE_SECTIONS.map((section) => {
          const inSection = rules.map((rule, index) => ({ rule, index })).filter(({ rule }) => ruleSection(rule) === section.id);
          if (inSection.length === 0) return null;
          return (
            <div key={section.id}>
              <div className="mb-1.5 text-xs font-semibold text-[#222B45]">{section.label}</div>
              <ul className="space-y-1.5">
                {inSection.map(({ rule, index }) => {
                  const previous = rules[index - 1];
                  const linked = !!rule.group && rule.group === previous?.group;
                  return (
                    <li key={rule.key}>
                      {linked && <div className="pl-3 pb-1 text-[11px] font-semibold text-genie-primary">or</div>}
                      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-[#E5EAF0] bg-[#FAFCFF] px-3 py-2">
                        {editing?.key === rule.key ? (
                          <input
                            autoFocus
                            type="text"
                            value={editing.text}
                            onChange={(e) => setEditing({ key: rule.key, text: e.target.value })}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") commitEditing();
                              if (e.key === "Escape") setEditing(null);
                            }}
                            onBlur={commitEditing}
                            aria-label="Condition"
                            className={`${inputClass} min-w-0 flex-1 basis-64`}
                          />
                        ) : rule.kind === "ai" ? (
                          <button
                            type="button"
                            onClick={() => setEditing({ key: rule.key, text: rule.text })}
                            title="Click to reword this condition"
                            className="min-w-0 flex-1 text-left text-sm text-[#222B45] hover:text-genie-primary transition-colors"
                          >
                            {describeRule(rule)}
                          </button>
                        ) : (
                          <span className="min-w-0 flex-1 text-sm text-[#222B45]">{describeRule(rule)}</span>
                        )}
                        <button
                          type="button"
                          onClick={() => patchRule(rule.key, { must: !rule.must })}
                          aria-pressed={rule.must}
                          title={rule.must ? "Conversations that fail this are left out" : "Only affects the match score"}
                          className={`${chipButtonClass} ${
                            rule.must
                              ? "border-genie-primary/40 bg-genie-primary/10 text-genie-primary"
                              : "border-[#E5EAF0] bg-white text-[#64748B] hover:border-genie-primary/40 hover:text-genie-primary"
                          }`}
                        >
                          {rule.must ? "Must have" : "Nice to have"}
                        </button>
                        {previous && ruleSection(previous) === section.id && (
                          <button
                            type="button"
                            onClick={() => toggleLink(index)}
                            title={linked ? "Require this on its own again" : "Either this or the condition above is enough"}
                            aria-label={linked ? "Unlink from the condition above" : "Link with the condition above"}
                            className="inline-flex min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 items-center justify-center rounded-lg p-1.5 text-[#B6C0CC] hover:text-genie-primary transition-colors"
                          >
                            {linked ? <Unlink className="h-3.5 w-3.5" /> : <Link2 className="h-3.5 w-3.5" />}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => removeRule(rule.key)}
                          aria-label="Remove condition"
                          title="Remove condition"
                          className="inline-flex min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 items-center justify-center rounded-lg p-1.5 text-[#B6C0CC] hover:text-[#E5484D] transition-colors"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })
      )}

      {adding ? (
        <AddCondition kind={adding} columns={view.column_schema ?? []} onAdd={addRule} onCancel={() => setAdding(null)} />
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <Plus className="h-3.5 w-3.5 text-genie-primary" />
          <select
            value=""
            onChange={(e) => setAdding(e.target.value as AddKind)}
            aria-label="Add a condition"
            className={`${inputClass} max-w-full`}
          >
            <option value="" disabled>
              Add a condition…
            </option>
            {ADD_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {usesClockTime && (
        <label className="flex flex-wrap items-center gap-2 text-xs text-[#64748B]">
          <span className="font-semibold text-[#222B45]">Days and hours are read in</span>
          <select
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            aria-label="Timezone"
            className={`${inputClass} max-w-full`}
          >
            {timezones.map((tz) => (
              <option key={tz} value={tz}>
                {tz.replace(/_/g, " ")}
              </option>
            ))}
          </select>
        </label>
      )}

      <div>
        <div className="flex items-center justify-between text-xs">
          <span className="font-semibold text-[#222B45]">Minimum match</span>
          <span className="font-semibold text-genie-primary">{threshold}</span>
        </div>
        <Slider
          className="mt-2 max-w-md"
          min={0}
          max={100}
          step={5}
          value={[threshold]}
          onValueChange={([value]) => setThreshold(value)}
          aria-label="Minimum match score"
        />
        <p className="mt-1.5 text-[11px] text-[#94A3B8]">
          Higher is stricter. Lower it if good conversations are being left out.
        </p>
      </div>

      {rulesDirty && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={saveRules}
            disabled={isSaving}
            className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-white bg-gradient-to-r from-genie-primary to-genie-secondary shadow-lg shadow-genie-primary/30 disabled:opacity-50"
          >
            {isSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
            Save conditions
          </button>
          <button
            type="button"
            onClick={resetRules}
            disabled={isSaving}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold text-[#64748B] hover:text-[#222B45]"
          >
            Cancel
          </button>
        </div>
      )}

      <div>
        <label htmlFor="view-prompt" className="text-xs font-semibold text-[#222B45]">
          View description
        </label>
        <textarea
          id="view-prompt"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={3}
          disabled={isSaving}
          className={`${inputClass} mt-1.5 w-full resize-y disabled:opacity-60`}
        />
        {promptDirty && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => onSave({ prompt: prompt.trim() })}
              disabled={isSaving}
              className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-white bg-gradient-to-r from-genie-primary to-genie-secondary shadow-lg shadow-genie-primary/30 disabled:opacity-50"
            >
              {isSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
              Save description
            </button>
            <button
              type="button"
              onClick={() => setPrompt(view.prompt)}
              disabled={isSaving}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold text-[#64748B] hover:text-[#222B45]"
            >
              Cancel
            </button>
            <span className="text-[11px] text-[#94A3B8]">
              The columns and the conditions above are redone from the new description, and every conversation is
              checked again. Columns that still fit keep their values; ones that no longer apply are removed.
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

interface AddConditionProps {
  kind: AddKind;
  columns: InboxView["column_schema"];
  onAdd: (rule: InboxViewRuleDraft) => void;
  onCancel: () => void;
}

const AddCondition = ({ kind, columns, onAdd, onCancel }: AddConditionProps) => {
  const [text, setText] = useState("");
  const [negate, setNegate] = useState(false);
  const [column, setColumn] = useState(columns[0]?.name ?? "");
  const [fieldOp, setFieldOp] = useState<InboxViewFieldOp>("filled");
  const [value, setValue] = useState("");
  const [op, setOp] = useState<"gte" | "lte">("gte");
  const [amount, setAmount] = useState("");
  const [weekdays, setWeekdays] = useState<number[]>([1, 2, 3, 4, 5]);
  const [from, setFrom] = useState("08:30");
  const [to, setTo] = useState("17:00");
  const [outside, setOutside] = useState(false);
  const [score, setScore] = useState<"urgency" | "intent">("urgency");

  const number = Number(amount);
  const hasNumber = amount.trim() !== "" && Number.isFinite(number) && number >= 0;
  const fieldNeedsValue = FIELD_OPS.find((o) => o.value === fieldOp)?.needsValue ?? false;
  const columnType = columns.find((c) => c.name === column)?.type;

  const draft = ((): InboxViewRuleDraft | null => {
    switch (kind) {
      case "ai":
        return text.trim() ? { kind: "ai", text: text.trim(), must: true, ...(negate ? { negate: true } : {}) } : null;
      case "field": {
        if (!column) return null;
        if (!fieldNeedsValue) {
          const format = fieldOp === "filled" && (columnType === "phone" || columnType === "email") ? columnType : undefined;
          return { kind: "field", column, op: fieldOp, must: true, ...(format ? { format } : {}) };
        }
        if (!value.trim()) return null;
        if (fieldOp === "gte" || fieldOp === "lte") {
          return Number.isFinite(Number(value)) ? { kind: "field", column, op: fieldOp, value: Number(value), must: true } : null;
        }
        const parsed = fieldOp === "equals_any" ? value.split(",").map((v) => v.trim()).filter(Boolean) : value.trim();
        return { kind: "field", column, op: fieldOp, value: parsed, must: true };
      }
      case "occurred":
        return weekdays.length > 0 && from !== to
          ? { kind: "time", metric: "occurred", weekdays, hours: { from, to }, must: true, ...(outside ? { outside: true } : {}) }
          : null;
      case "duration":
        return hasNumber ? { kind: "time", metric: "duration", op, seconds: Math.round(number * 60), must: true } : null;
      case "caller_turns":
        return hasNumber ? { kind: "time", metric: "caller_turns", op, value: Math.round(number), must: true } : null;
      case "deadline":
        return { kind: "time", metric: "deadline", must: true, ...(hasNumber && number > 0 ? { within_days: Math.round(number) } : {}) };
      case "age":
        return hasNumber && number > 0 ? { kind: "time", metric: "age", hours: number, must: true } : null;
      case "score":
        return hasNumber && number <= 100 ? { kind: "score", score, op, value: number, must: true } : null;
    }
  })();

  const opSelect = (
    <select value={op} onChange={(e) => setOp(e.target.value as "gte" | "lte")} className={inputClass} aria-label="Comparison">
      <option value="gte">at least</option>
      <option value="lte">at most</option>
    </select>
  );
  const amountInput = (label: string, placeholder: string) => (
    <input
      type="number"
      min={0}
      value={amount}
      onChange={(e) => setAmount(e.target.value)}
      placeholder={placeholder}
      aria-label={label}
      className={`${inputClass} w-24`}
    />
  );

  return (
    <div className="rounded-xl border border-genie-primary/30 bg-white p-3">
      <div className="mb-2 text-xs font-semibold text-[#222B45]">{ADD_OPTIONS.find((o) => o.value === kind)?.label}</div>
      <div className="flex flex-wrap items-center gap-2 text-sm text-[#64748B]">
        {kind === "ai" && (
          <>
            <input
              autoFocus
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && draft) onAdd(draft);
              }}
              placeholder="e.g. The caller asked for a callback from the service team"
              aria-label="Condition"
              className={`${inputClass} min-w-0 flex-1 basis-64`}
            />
            <label className="inline-flex min-h-[44px] sm:min-h-0 items-center gap-1.5 text-xs">
              <input type="checkbox" checked={negate} onChange={(e) => setNegate(e.target.checked)} />
              Leave these out instead
            </label>
          </>
        )}

        {kind === "field" && (
          <>
            <select value={column} onChange={(e) => setColumn(e.target.value)} className={inputClass} aria-label="Column">
              {columns.map((c) => (
                <option key={c.name} value={c.name}>
                  {c.name}
                </option>
              ))}
            </select>
            <select
              value={fieldOp}
              onChange={(e) => setFieldOp(e.target.value as InboxViewFieldOp)}
              className={inputClass}
              aria-label="Check"
            >
              {FIELD_OPS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            {fieldNeedsValue && (
              <input
                type="text"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="Value"
                aria-label="Value"
                className={`${inputClass} min-w-0 flex-1 basis-40`}
              />
            )}
          </>
        )}

        {kind === "occurred" && (
          <>
            <div className="flex flex-wrap gap-1">
              {WEEKDAYS.map((day, index) => {
                const on = weekdays.includes(index);
                return (
                  <button
                    key={day}
                    type="button"
                    aria-pressed={on}
                    onClick={() => setWeekdays((prev) => (on ? prev.filter((d) => d !== index) : [...prev, index].sort()))}
                    className={`${chipButtonClass} ${
                      on
                        ? "border-genie-primary/40 bg-genie-primary/10 text-genie-primary"
                        : "border-[#E5EAF0] bg-white text-[#64748B]"
                    }`}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
            <input type="time" value={from} onChange={(e) => setFrom(e.target.value)} className={inputClass} aria-label="From" />
            <span>to</span>
            <input type="time" value={to} onChange={(e) => setTo(e.target.value)} className={inputClass} aria-label="To" />
            <label className="inline-flex min-h-[44px] sm:min-h-0 items-center gap-1.5 text-xs">
              <input type="checkbox" checked={outside} onChange={(e) => setOutside(e.target.checked)} />
              Outside these hours (after hours)
            </label>
          </>
        )}

        {kind === "duration" && (
          <>
            <span>Lasted</span>
            {opSelect}
            {amountInput("Minutes", "2")}
            <span>minutes</span>
          </>
        )}

        {kind === "caller_turns" && (
          <>
            <span>Caller spoke</span>
            {opSelect}
            {amountInput("Times", "3")}
            <span>times</span>
          </>
        )}

        {kind === "deadline" && (
          <>
            <span>Caller needs it within</span>
            {amountInput("Days", "any")}
            <span>days (leave empty for any deadline)</span>
          </>
        )}

        {kind === "age" && (
          <>
            <span>Show only once it has waited</span>
            {amountInput("Hours", "24")}
            <span>hours and is not marked handled</span>
          </>
        )}

        {kind === "score" && (
          <>
            <select
              value={score}
              onChange={(e) => setScore(e.target.value as "urgency" | "intent")}
              className={inputClass}
              aria-label="Score"
            >
              <option value="urgency">Urgency</option>
              <option value="intent">Buying intent</option>
            </select>
            {opSelect}
            {amountInput("Score", "70")}
            <span>out of 100</span>
          </>
        )}
      </div>

      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          onClick={() => draft && onAdd(draft)}
          disabled={!draft}
          className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold bg-genie-primary/10 text-genie-primary disabled:opacity-50"
        >
          <Plus className="h-3 w-3" />
          Add
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 rounded-lg text-xs font-semibold text-[#64748B] hover:text-[#222B45]"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};
